import {Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import { OverlayHelper } from '../../../../../helper/overlay.helper';
import { UserTokenService } from '../../../../../services/user-service/user-token.service';
import { ProfileData } from '../../../../../models/profile/profile.model';
import { AdminMandant } from '../../../../../models/cloud/cloud.model';
import {CONTACTS_DATA_SERVICE, ContactsDataService} from '../../../../../services/contacts/contacts-data.service';
import {takeUntil} from 'rxjs/operators';
import {versionLog} from '../../../../../helper/update-constant.helper';
import {ILogOut} from '../../../../../models/contacts/contacts.model';

@Component({
  selector: 'fi-user-menu',
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss'],
  standalone: false,
})
export class UserMenuComponent implements OnChanges, OnDestroy {
  @Input() isUserMenuActive: boolean;
  @Input() isBurgerMenuActive: boolean;
  @Input() isMobile = false;
  @Input() profileData: ProfileData;
  @Input() adminMandant?: AdminMandant;
  @Input() enableReporting: boolean;


  @Output() setActiveItem = new EventEmitter<'user' | 'burger' | ''>();
  isOpened = false;

  private subscriptionEnd$ = new EventEmitter<void>();

  constructor(
    @Inject(CONTACTS_DATA_SERVICE) private contactsDataService: ContactsDataService,
    private readonly userTokenService: UserTokenService
  ) {
  }

  ngOnChanges( changes: SimpleChanges ): void {
    this.isOpened = this.isUserMenuActive || this.isBurgerMenuActive;
  }

  userIconClick( $event ): void {
    $event.target?.blur();

    if ( !this.isOpened ) {
      this.setActiveItem.emit('user');
      OverlayHelper.fixBody();
    } else {
      this.closeOverlay($event);
    }
  }

  burgerIconClick( $event ): void {
    $event.target?.blur();

    if ( !this.isOpened ) {
      this.setActiveItem.emit('burger');
      OverlayHelper.fixBody();
    } else {
      this.closeOverlay($event);
    }
  }

  closeOverlay( $event ): void {
    $event.stopPropagation();

    this.isOpened = false;

    /**
     * Timout for menu dropdown animation
     */
    setTimeout(_ => {
      this.setActiveItem.emit('');
      OverlayHelper.unfixBody();
    }, 300);
  }

  logout( $event ): void {
    $event.stopPropagation();
    const token = this.userTokenService.getToken();
    this.contactsDataService.logOut(token).pipe(takeUntil(this.subscriptionEnd$))
      .subscribe(({success, data}) => {
        this.finishLogout(success ? data : null);
      });

    this.closeOverlay($event);
  }

  private finishLogout( data: ILogOut | null ): void {
    if (data?.logoutUrl) {
      this.clearSession();
      this.submitEndSessionForm(data);
      return;
    }

    this.clearSession();
  }

  private clearSession(): void {
    this.userTokenService.removeToken();
    this.userTokenService.removeId();
    this.userTokenService.removeName();
    this.userTokenService.removeLocale();
    this.userTokenService.removeCurrency();
    localStorage.setItem('changelogSkipped', versionLog.currentVersion);
    sessionStorage.clear();
  }

  private submitEndSessionForm( data: ILogOut ): void {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = data.logoutUrl;
    form.enctype = 'application/x-www-form-urlencoded';
    form.style.display = 'none';

    this.appendHiddenInput(form, 'id_token_hint', data.id_token_hint);
    this.appendHiddenInput(form, 'post_logout_redirect_uri', data.post_logout_redirect_uri);

    document.body.appendChild(form);
    form.submit();
  }

  private appendHiddenInput( form: HTMLFormElement, name: string, value?: string ): void {
    if (!value) {
      return;
    }

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  ngOnDestroy(): void {
    this.subscriptionEnd$?.emit();
    this.subscriptionEnd$?.complete?.();
  }

  get profileImage(): string {
    return this.profileData?.User.AvatarURL ? ('url(' + this.profileData.User.AvatarURL + ')') : '';
  }
}
